import { useState, useEffect, useRef } from 'react';
import { SolutionCTAPillar } from '@/types/scenarios/solution';

interface SolutionFinalCTAProps {
  title?: string;
  subtitle?: string;
  pillars: SolutionCTAPillar[];
  backgroundImage?: string;
}

const TypewriterPillar = ({
  pillar,
  isActive,
  isComplete,
  onComplete
}: {
  pillar: SolutionCTAPillar;
  isActive: boolean;
  isComplete: boolean;
  onComplete: () => void;
}) => {
  const [displayText, setDisplayText] = useState('');
  const [showDescription, setShowDescription] = useState(false);
  const typingSpeed = 30;

  useEffect(() => {
    if (!isActive || isComplete) return;

    const targetText = pillar.title;
    let currentIndex = 0;

    const typeInterval = setInterval(() => {
      if (currentIndex < targetText.length) {
        setDisplayText(targetText.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typeInterval);
        setShowDescription(true);
        setTimeout(onComplete, 300);
      }
    }, typingSpeed);

    return () => clearInterval(typeInterval);
  }, [isActive, isComplete, pillar.title, onComplete]);

  useEffect(() => {
    if (isComplete) {
      setDisplayText(pillar.title);
      setShowDescription(true);
    }
  }, [isComplete, pillar.title]);

  // Handle line breaks in title (e.g., "Always\nOn")
  const formattedText = displayText;

  return (
    <div className="text-center p-4 sm:p-6 group hover:scale-105 transition-transform duration-300">
      <h3
        className={`text-2xl sm:text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${pillar.gradient} mb-3 sm:mb-4 drop-shadow-lg whitespace-pre-line min-h-[2.5em] sm:min-h-[2.8em] md:min-h-[3em] flex items-center justify-center`}
      >
        {formattedText}
        {isActive && !isComplete && <span className="animate-pulse text-white/80">|</span>}
      </h3>
      <p className={`text-gray-100 text-sm sm:text-base md:text-lg drop-shadow-md transition-opacity duration-500 ${showDescription ? 'opacity-100' : 'opacity-0'}`}>
        {pillar.description}
      </p>
    </div>
  );
};

export const SolutionFinalCTA = ({
  title = "Your Business Never Sleeps",
  subtitle = "While you rest, your Genie captures every lead, handles every question, and ensures no opportunity slips through the cracks.",
  pillars,
  backgroundImage = "/optimized/tmphn9da0yi.webp",
}: SolutionFinalCTAProps) => {
  const [activePillarIndex, setActivePillarIndex] = useState(-1);
  const [completedPillars, setCompletedPillars] = useState<number[]>([]);
  const [hasStarted, setHasStarted] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasStarted) {
          setHasStarted(true);
          setActivePillarIndex(0);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [hasStarted]);

  const handlePillarComplete = (index: number) => {
    setCompletedPillars(prev => [...prev, index]);
    if (index < pillars.length - 1) {
      setActivePillarIndex(index + 1);
    }
  };

  if (!pillars || pillars.length === 0) {
    return null;
  }

  return (
    <section id="solution-cta" className="relative w-full py-12 sm:py-16 md:py-20 lg:py-24 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src={backgroundImage}
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/75 to-black/80" />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Title & Subtitle */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4 sm:mb-6 drop-shadow-2xl">
            {title}
          </h2>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed sm:leading-8 text-gray-100 max-w-4xl mx-auto drop-shadow-lg px-2 sm:px-0">
            {subtitle}
          </p>
        </div>

        {/* Pillars with Typewriter Animation */}
        <div ref={sectionRef} className="mb-12 sm:mb-16 md:mb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 md:gap-10 max-w-6xl mx-auto">
            {pillars.map((pillar, index) => (
              <TypewriterPillar
                key={pillar.title}
                pillar={pillar}
                isActive={activePillarIndex === index}
                isComplete={completedPillars.includes(index)}
                onComplete={() => handlePillarComplete(index)}
              />
            ))}
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="text-center mt-4 sm:mt-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 md:gap-6 px-4 sm:px-0">
            <a
              href="/live-agent/business-onboarding"
              className="px-6 sm:px-8 md:px-10 py-3.5 sm:py-4 bg-transparent hover:bg-white/10 active:bg-white/20 text-white hover:text-gray-100 font-semibold rounded-xl border-2 border-white/50 hover:border-white/70 transition-all duration-300 text-sm sm:text-base md:text-lg w-full sm:w-auto text-center backdrop-blur-sm max-w-[280px] sm:max-w-none min-h-[48px] touch-manipulation"
            >
              Talk To Me
            </a>
            <a
              href="/signup"
              className="px-6 sm:px-8 md:px-10 py-3.5 sm:py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 active:from-purple-800 active:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-sm sm:text-base md:text-lg w-full sm:w-auto text-center max-w-[280px] sm:max-w-none min-h-[48px] touch-manipulation"
            >
              Start For Free
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
